import * as React from 'react';
import { SkeletonRows, TextSkeleton, type CardProps } from '@chess/api-insights/charts';
import { lineTail, numberedMove as numbered } from '@chess/api-insights';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import type { Fen, San, Uci } from '@chess/api-model';
import type { OpeningHabit, OpeningHabitsResult } from './OpeningHabitsAggregator.ts';
import './OpeningHabitsCard.css';

// "Positions you keep getting wrong" (2026-10-09; the Openings screen only,
// under its top cards): the opening positions the reader went wrong in, in
// more than one game, worst first. A row names the position (the opening and
// the last moves to it), the move played there most often, the engine's move,
// the games and the pawns given up on average. The whole row opens the
// newest game's review just before the move, where the board's arrows show
// the move played against the engine's. The game count is not a link yet:
// the library cannot show a set of games by id (no URL parameter, no chip).
// Drawn from props only; its frame (wide) is the section's (`registerCard`).

export type OpeningHabitsCardProps = CardProps<
  { result: OpeningHabitsResult },
  {
    /** Opens a game's review at a ply. */
    review?: { href(path: string, ply: number): string; open(path: string, ply: number): void };
    /** The engine's move as SAN; the UCI is shown without it. */
    sanOf?: (fen: Fen, uci: Uci) => San | undefined;
  }
>;

export const TITLE = 'Positions you keep getting wrong';
const ASIDE = 'opening moves, more than one game';
const COLOUR = { white: 'as White', black: 'as Black' } as const;
const games = (n: number) => `${n} ${n === 1 ? 'game' : 'games'}`;

const Row: React.FC<{ habit: OpeningHabit } & Pick<OpeningHabitsCardProps, 'review' | 'sanOf'>> = ({ habit, review, sanOf }) => {
  const newest = habit.games[0];
  const ply = newest.ply;
  // The position before the move: the review draws the move played from it and the engine's.
  const at = ply - 1;
  const path = review && newest.reviewPath;
  const open = path ? () => review!.open(path, at) : undefined;
  const position = (
    <>
      <span className="habit-opening">{habit.opening ?? 'Opening'} · {COLOUR[habit.colour]}</span>
      <span className="habit-line">{lineTail(habit.line) || 'the start'}</span>
    </>
  );
  return (
    // The whole row opens the review; the position is its link for the keyboard and a new tab.
    <div
      className={`habit-row${open ? ' habit-open' : ''}`}
      title={open ? 'Opens the newest of these games at this position, your move and the engine\'s drawn on the board' : undefined}
      data-track={open ? 'openings.habits.open' : undefined}
      onClick={(e) => {
        if (!open || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        open();
      }}>
      {path ? (
        <a
          className="habit-position"
          href={review!.href(path, at)}
          data-track="openings.habits.open"
          onClick={(e) => {
            if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return e.stopPropagation();
            e.preventDefault();
            open!();
          }}>
          {position}
        </a>
      ) : (
        <span className="habit-position">{position}</span>
      )}
      <span className="habit-played" title={`Played in ${games(habit.playedGames)} of ${habit.games.length}`}>
        {numbered(ply, habit.played)}
        {habit.playedGames > 1 ? <span className="dash-muted"> ×{habit.playedGames}</span> : null}
      </span>
      <span className="habit-best">{numbered(ply, sanOf?.(habit.fen, habit.best) ?? habit.best)}</span>
      <span className="habit-games">{habit.games.length}</span>
      <span className="habit-loss">−{habit.loss.toFixed(2)}</span>
    </div>
  );
};

const Head: React.FC = () => (
  <div className="habit-row habit-head" aria-hidden>
    <span>Position</span>
    <span>You played</span>
    <span>Engine</span>
    <span className="habit-games">Games</span>
    <span className="habit-loss">Lost</span>
  </div>
);

export const OpeningHabitsCard: React.FC<OpeningHabitsCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <SkeletonRows labels={[undefined, undefined, undefined]} value="0" />
        <CardNote>
          <TextSkeleton sample="3 positions in 120 scanned games · a row opens its newest game at the move" />
        </CardNote>
      </>
    );
  const { result, review, sanOf } = props;
  if (!result.scanned)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardEmpty>Scan games to find the opening positions you go wrong in.</CardEmpty>
      </>
    );
  if (!result.rows.length)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardEmpty>No opening position went wrong in more than one game in the last 30 days.</CardEmpty>
        <CardNote>{`of ${games(result.scanned)} scanned`}</CardNote>
      </>
    );
  const more = result.habits > result.rows.length ? `the worst ${result.rows.length} of ${result.habits} positions` : `${result.habits} ${result.habits === 1 ? 'position' : 'positions'}`;
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      <div className="habit-rows">
        <Head />
        {result.rows.map((h) => (
          <Row key={h.fen} habit={h} review={review} sanOf={sanOf} />
        ))}
      </div>
      <CardNote>{`${more} in ${games(result.scanned)} scanned · a row opens its newest game at the move; lost is pawns on average`}</CardNote>
    </>
  );
};
